import { Reference } from "@apollo/client";
import { ApolloCache } from "@apollo/client/cache";
import { Modifier } from "@apollo/client/cache/core/types/common";
import { FindMatterQuery } from "~/gql/graphql";
import { graphql } from "~/gql";

export const FIND_MATTER = graphql(/* GraphQL */ `
  query findMatter($id: Float!) {
    findMatter(id: $id) {
      __typename
      id
      reference
      isFavourite
      title
      status
      reference
      createdAt
      updatedAt
      template
      parentMatterId
      currency {
        id
        code
        symbol
      }
      parent {
        id
        title
        matters {
          id
          title
        }
      }
      externalMatterId
      externalMatter {
        id
        title
        status
        leadFeeEarner {
          id
          name
        }
        client {
          id
          name
          externalReference
          externalId
          type
        }
        externalReference
        organisationalUnit {
          __typename
          id
          name
          parent {
            id
            name
          }
        }
        currency {
          id
          code
          symbol
        }
      }
      externalReference
      createdBy {
        id
        userName
        displayName
      }
      client {
        id
        name
        externalReference
        externalId
        type
      }
      priceOptions {
        id
        estimatedGpm
        estimatedSuggested
        estimatedChosen
        estimatedRangeGpm
        estimatedRangeSuggestedLower
        estimatedRangeSuggestedUpper
        estimatedRangeChosenLower
        estimatedRangeChosenUpper
        fixedGpm
        fixedSuggested
        fixedChosen
        cappedChosen
        cappedGpm
        cappedSuggested
        feeRiskLower
        feeRiskUpper
        topDownFeeTarget
        topDownHoursTarget
        topDownRateDiscount
      }
      technologyCharges {
        autoFocus @client
        ...TechnologyChargeParts
      }
      disbursements {
        __typename
        autoFocus @client
        id
        name
        amount
        vatStatus
        order
      }
      uplifts {
        __typename
        autoFocus @client
        id
        amount
        type
        order
        baseUplift {
          id
          reason
        }
        includeOnProposal
      }
      discounts {
        __typename
        autoFocus @client
        id
        reason
        amount
        type
        order
      }
      phases {
        __typename
        autoFocus @client
        id
        title
        html
        createdAt
        matterId
        updatedAt
        description
        order
        markedComplete
        completionPercentage
        startDate
        deadlineDate
        tasks {
          id
          order
          title
          html
          createdAt
          phaseId
          updatedAt
          markedComplete
          startDate
          deadlineDate
        }
        priceOptions {
          id
          estimatedGpm
          estimatedSuggested
          estimatedChosen
          estimatedRangeGpm
          estimatedRangeSuggestedLower
          estimatedRangeSuggestedUpper
          estimatedRangeChosenLower
          estimatedRangeChosenUpper
          fixedGpm
          fixedSuggested
          fixedChosen
          cappedChosen
          cappedGpm
          cappedSuggested
          topDownFeeTarget
          topDownHoursTarget
          topDownRateDiscount
        }
        __typename
        id
        title
        order
        work {
          __typename
          id
          phaseId
          timeLower
          timeUpper
          timeSingle
          workEffort
          topDownRateDiscount
          price {
            lower
            upper
            single
          }
          selectedRate {
            id
            description
            amount
            clientId
            feeEarnerId
            feeEarnerLevelId
            deprecated
            currency {
              id
              code
              symbol
            }
          }
          feeEarnerLevel {
            id
            description
            order
          }
          feeEarner {
            id
            name
            deprecated
            feeEarnerLevel {
              id
              description
              order
            }
            practiceArea {
              id
              name
            }
          }
          cost {
            lower
            upper
            single
          }
          costRate
          price {
            lower
            upper
            single
          }
        }
      }
      work {
        __typename
        id
        phaseId
        timeLower
        timeUpper
        timeSingle
        workEffort
        topDownRateDiscount
        price {
          lower
          upper
          single
        }
        selectedRate {
          id
          description
          amount
          clientId
          feeEarnerId
          feeEarnerLevelId
          deprecated
          currency {
            id
            code
            symbol
          }
        }
        feeEarnerLevel {
          id
          description
          order
        }
        feeEarner {
          id
          name
          deprecated
          feeEarnerLevel {
            id
            description
            order
          }
          rates {
            id
            description
            amount
            clientId
            feeEarnerId
            feeEarnerLevelId
            deprecated
            currency {
              id
              code
              symbol
            }
          }
          practiceArea {
            id
            name
          }
        }
        cost {
          lower
          upper
          single
        }
        costRate
        price {
          lower
          upper
          single
        }
      }
      organisationalUnit {
        __typename
        id
        name
        parent {
          id
          name
        }
      }
      leadFeeEarner {
        __typename
        id
        name
        feeEarnerLevel {
          id
        }
      }
      pricingType
      pricingLevel
      pricingTarget
      taxStatus
      includeTasksInProposalOutput
      includeRatesInProposalOutput
      includeFeeEarnersInProposalOutput
      gpmDangerThreshold {
        gpmDangerThreshold
        gpmWarningThreshold
        source
      }
      disbursements {
        ...DisbursementParts
      }
      brand {
        ...BrandParts
      }
      teamMemberships {
        ...TeamMembershipParts
      }
      feeRiskInclude
      feeRiskCappedFeeInclude
      waiveAdminCharge
      waiveAdminChargeReason
      officeLocationId
      officeLocation {
        id
        name
        countryCode
        taxLabel
        taxLevelPercent
      }
      confidential
      completionPercentage
      startDate
      deadlineDate
      pinnedMatters {
        id
      }
      aiGeneratedMatterSummary
    }
  }
`);

export function updateFindMatterCache(
  cache: ApolloCache<any>,
  matterId: number,
  update: (findMatter: any) => Partial<FindMatterQuery>
) {
  const { findMatter } =
    cache.readQuery({
      query: FIND_MATTER,
      variables: { id: matterId },
    }) || {};
  if (findMatter) {
    cache.writeQuery({
      query: FIND_MATTER,
      variables: { id: matterId },
      data: {
        findMatter: Object.assign({}, findMatter, update(findMatter)),
      },
    });
  }
}

export function modifyCachedMatter(
  cache: ApolloCache<any>,
  matterId: number,
  fieldModifiers: {
    phases: Modifier<Reference[]>;
  }
) {
  cache.modify({
    id: `Matter:${matterId}`,
    fields: fieldModifiers,
  });
}

export function modifyCachedPhase(
  cache: ApolloCache<any>,
  phaseId: number,
  fieldModifiers: {
    tasks: Modifier<Reference[]>;
  }
) {
  cache.modify({
    id: `Phase:${phaseId}`,
    fields: fieldModifiers,
  });
}
